import { datadogLogs, StatusType } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { angularPlugin } from '@datadog/browser-rum-angular';

import { jsConfig } from '../config/jsConfig';
import { MonitoringContext, MonitoringMessageOptions, MonitoringModule } from './monitoring.interface';
import { MONITORED_CONSOLE_MARKER } from './console-monitoring';
import { deployEnv, isDatadogConfigured } from './monitoring-config';
import { isErrorLevel, isFinnairMonitoringUrl, isIgnoredErrorMessage, maskSensitive } from './scrub';
import { buildErrorContext, getSessionId } from './error-context';
import { collectFinnairApiUrls, isTracingAllowed } from './traced-apis';

const SERVICE = 'www.finnair.com';
const DEFAULT_SITE = 'datadoghq.eu';

/**
 * Where the RUM/Logs session is persisted. `'cookie'` is the default cookie-backed session (used
 * once analytics consent is granted); `'memory'` keeps the session in memory only — no cookie and
 * no localStorage — so RUM can run before consent without writing anything to storage.
 */
export type DatadogSessionMode = 'cookie' | 'memory';

const toLogStatus = (level: MonitoringMessageOptions['level']): StatusType => {
  switch (level) {
    case 'fatal':
    case 'error':
      return StatusType.error;
    case 'warning':
      return StatusType.warn;
    case 'debug':
      return StatusType.debug;
    default:
      return StatusType.info;
  }
};

export class DatadogMonitoring implements MonitoringModule {
  private started = false;

  private get config(): typeof jsConfig.datadog {
    return jsConfig.datadog;
  }

  private get enabled(): boolean {
    return isDatadogConfigured(this.config) && typeof window !== 'undefined';
  }

  /**
   * No-op on the client. RUM is not initialised at bootstrap because it must stay behind consent:
   * the SDK writes its session cookie on `init()`, so initialisation is deferred to {@link start},
   * which the consent orchestrator calls once it knows whether cookies are allowed. Errors before
   * that point are still covered by the Sentry backend, which initialises here as usual.
   */
  init(): void {
    // Intentionally empty — see start().
  }

  /**
   * Initialises RUM and Logs with the persistence mode chosen from consent. `'memory'` runs without
   * any cookie/localStorage (pre-consent); `'cookie'` is the full session (analytics consent granted).
   * Idempotent — the Browser SDK ignores a second `init()`, and the mode cannot change afterwards.
   */
  start(mode: DatadogSessionMode): void {
    if (!this.enabled || this.started) {
      return;
    }
    this.started = true;

    const common = {
      clientToken: this.config.clientToken,
      site: this.config.site ?? DEFAULT_SITE,
      service: SERVICE,
      env: deployEnv(),
      version: jsConfig.versionInfo.releaseGitCommitHash,
    };

    const knownApis = collectFinnairApiUrls(jsConfig);

    // session.id starts best-effort (usually N/A before the first dapi request) and is re-synced on
    // each event once the interceptor has generated the real id, mirroring Sentry's per-event tag.
    let sessionId = getSessionId();

    datadogRum.init({
      ...common,
      applicationId: this.config.applicationId,
      // 'memory' keeps the session out of cookies/storage before consent; 'cookie' is the default
      // persisted session once analytics consent is granted. Data is sent in both modes (the SDK
      // default trackingConsent 'granted' is left as-is).
      sessionPersistence: mode,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      startSessionReplayRecordingManually: true,
      defaultPrivacyLevel: 'mask-user-input',
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      plugins: [angularPlugin({ router: true })],
      allowedTracingUrls: [
        { match: (url: string) => isTracingAllowed(url, knownApis), propagatorTypes: ['tracecontext'] },
      ],
      beforeSend: (event) => {
        const currentSessionId = getSessionId();
        if (currentSessionId !== sessionId) {
          sessionId = currentSessionId;
          datadogRum.setGlobalContextProperty('session.id', currentSessionId);
        }
        if (event.type === 'error' && isIgnoredErrorMessage(event.error.message)) {
          return false;
        }
        // RUM always collects `securitypolicyviolation` events as errors (source 'report'); they are noise here.
        if (event.type === 'error' && event.error.source === 'report' && event.error.csp) {
          return false;
        }
        // RUM auto-collects `console.error()` calls (source 'console'). Our console monitoring backend
        // tags the errors it mirrors with MONITORED_CONSOLE_MARKER, so drop only those — they duplicate
        // the richer error already reported by the Datadog backend via `addError` (source 'custom').
        // Untagged third-party console errors are kept so they still surface in RUM.
        if (
          event.type === 'error' &&
          event.error.source === 'console' &&
          event.error.message.includes(MONITORED_CONSOLE_MARKER)
        ) {
          return false;
        }
        if (event.view.url && !isFinnairMonitoringUrl(event.view.url)) {
          return false;
        }
        event.view.url = maskSensitive(event.view.url);
        if (event.type === 'resource' && event.resource.url) {
          event.resource.url = maskSensitive(event.resource.url);
        }
        return true;
      },
    });

    // Set first so it is present from the very first event; beforeSend keeps it current thereafter.
    datadogRum.setGlobalContextProperty('session.id', sessionId);
    // Record whether the session is cookie-backed (analytics consent granted) or memory-only (pre-consent).
    datadogRum.setGlobalContextProperty('session.persistence', mode);

    datadogLogs.init({
      ...common,
      // Same session store as RUM so Logs writes no cookie/storage in memory mode either.
      sessionPersistence: mode,
      forwardErrorsToLogs: false,
      sessionSampleRate: 100,
      beforeSend: (log) => {
        if (isIgnoredErrorMessage(log.message)) {
          return false;
        }
        log.message = maskSensitive(log.message);
        return true;
      },
    });
  }

  captureException(error: unknown, context?: MonitoringContext): void {
    if (!this.started) {
      return;
    }
    datadogRum.addError(error, { ...buildErrorContext(error), ...context });
  }

  captureMessage(message: string, options: MonitoringMessageOptions): void {
    if (!this.started) {
      return;
    }
    if (isErrorLevel(options.level)) {
      // error/warn go to RUM Error Tracking, wrapped so they carry a stack; the enriched, sanitized
      // context (level, HttpErrorResponse details, session id, …) rides along so it is filterable.
      const error = new Error(message);
      if (options.fingerprint) {
        // Custom grouping: the SDK reads `dd_fingerprint` off the error, so each fingerprint becomes
        // its own Error Tracking issue (with the real message as title) instead of a masked pattern.
        (error as Error & { dd_fingerprint?: string }).dd_fingerprint = options.fingerprint;
      }
      datadogRum.addError(error, buildErrorContext({ level: options.level, ...options.extra }));
    } else {
      datadogLogs.logger.log(message, options.extra, toLogStatus(options.level));
    }
  }

  action(name: string, context?: MonitoringContext): void {
    if (!this.started) {
      return;
    }
    datadogRum.addAction(name, context);
  }
}
